import React, { useState } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  Check,
  ChevronRight,
  Repeat,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { toast } from '../components/ui/Toast';
import {
  recurringExpensesApi,
  type RecurringExpense,
  type RecurringExpenseCreate,
  type RecurringExpenseUpdate,
  type RecurringInterval,
} from '../api/recurringExpenses';
import { formatEUR } from '../utils/format';

// =============================================
// Constants
// =============================================
const INTERVAL_LABELS: Record<RecurringInterval, string> = {
  weekly: 'Wöchentlich',
  monthly: 'Monatlich',
  quarterly: 'Vierteljährlich',
  yearly: 'Jährlich',
};

const INTERVAL_OPTIONS: { value: RecurringInterval; label: string }[] = [
  { value: 'weekly', label: 'Wöchentlich' },
  { value: 'monthly', label: 'Monatlich' },
  { value: 'quarterly', label: 'Vierteljährlich' },
  { value: 'yearly', label: 'Jährlich' },
];

const INTERVAL_BADGE_COLORS: Record<RecurringInterval, string> = {
  weekly: 'bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300',
  monthly: 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300',
  quarterly: 'bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300',
  yearly: 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
};

const ICON_OPTIONS = [
  { emoji: '\uD83C\uDFE0', label: 'Miete' },
  { emoji: '\uD83D\uDCFA', label: 'Netflix' },
  { emoji: '\uD83C\uDFB5', label: 'Musik' },
  { emoji: '\uD83D\uDCA1', label: 'Strom' },
  { emoji: '\uD83D\uDCF1', label: 'Handy' },
  { emoji: '\uD83C\uDFCB\uFE0F', label: 'Gym' },
  { emoji: '\uD83D\uDE97', label: 'Auto' },
  { emoji: '\uD83D\uDC8A', label: 'Versicherung' },
  { emoji: '\uD83D\uDCE6', label: 'Sonstiges' },
];

const CATEGORY_OPTIONS = [
  'Wohnen',
  'Unterhaltung',
  'Kommunikation',
  'Transport',
  'Gesundheit',
  'Versicherung',
  'Sport & Freizeit',
  'Sonstiges',
];

// =============================================
// Helpers
// =============================================

/**
 * Returns the monthly equivalent of an amount for a given interval.
 */
function toMonthlyAmount(amount: number, interval: RecurringInterval): number {
  switch (interval) {
    case 'weekly':
      return amount * 52 / 12;
    case 'monthly':
      return amount;
    case 'quarterly':
      return amount / 3;
    case 'yearly':
      return amount / 12;
  }
}

/**
 * Compute summary from a list of expenses (client-side fallback when API has no summary endpoint).
 */
function computeSummary(expenses: RecurringExpense[]) {
  const active = expenses.filter((e) => e.is_active);
  const monthly_total = active.reduce(
    (sum, e) => sum + toMonthlyAmount(Number(e.amount) || 0, e.interval),
    0,
  );
  const yearly_total = monthly_total * 12;
  const today = new Date();
  const in7 = new Date(today);
  in7.setDate(today.getDate() + 7);
  const due_soon_count = active.filter((e) => {
    const d = new Date(e.next_due_date);
    return d >= today && d <= in7;
  }).length;
  return { monthly_total, yearly_total, count: active.length, due_soon_count };
}

function isDueSoon(dateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  const diff = (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= 7;
}

function isOverdue(dateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  return due < today;
}

function formatDueDate(dateStr: string): string {
  const due = new Date(dateStr);
  return due.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

// =============================================
// Summary Header
// =============================================
interface SummaryHeaderProps {
  monthlyTotal: number;
  yearlyTotal: number;
  count: number;
}

const SummaryHeader: React.FC<SummaryHeaderProps> = ({ monthlyTotal, yearlyTotal, count }) => (
  <div className="animate-fade-in">
    <div className="flex items-start justify-between mb-1">
      <div>
        <h1 className="text-3xl font-bold text-neutral-800 dark:text-white">
          Fixkosten &amp; Abos
        </h1>
        <p className="text-neutral-400 dark:text-neutral-500 mt-1 text-sm">
          {count} {count === 1 ? 'aktive Ausgabe' : 'aktive Ausgaben'}
        </p>
      </div>
      <div className="hidden sm:block text-right">
        <div className="text-4xl animate-float">{'\uD83D\uDCB3'}</div>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-3 mt-5">
      {/* Monthly total */}
      <div className="relative overflow-hidden rounded-2xl p-5 text-white shadow-lg
                      bg-gradient-to-br from-indigo-500 via-indigo-600 to-violet-600
                      hover:-translate-y-1 hover:shadow-xl transition-all duration-300 animate-slide-up animate-fill-both"
        style={{ animationDelay: '100ms' }}
      >
        <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full" />
        <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-white/5 rounded-full" />
        <div className="relative z-10">
          <p className="text-sm font-medium text-white/80">Pro Monat</p>
          <p className="text-2xl sm:text-3xl font-bold font-mono tabular-nums mt-1 tracking-tight">
            {formatEUR(monthlyTotal)}
          </p>
        </div>
      </div>

      {/* Yearly total */}
      <div className="relative overflow-hidden rounded-2xl p-5 text-white shadow-lg
                      bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500
                      hover:-translate-y-1 hover:shadow-xl transition-all duration-300 animate-slide-up animate-fill-both"
        style={{ animationDelay: '200ms' }}
      >
        <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full" />
        <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-white/5 rounded-full" />
        <div className="relative z-10">
          <p className="text-sm font-medium text-white/80">Pro Jahr</p>
          <p className="text-2xl sm:text-3xl font-bold font-mono tabular-nums mt-1 tracking-tight">
            {formatEUR(yearlyTotal)}
          </p>
        </div>
      </div>
    </div>
  </div>
);

// =============================================
// Due Soon Card
// =============================================
interface DueSoonCardProps {
  expense: RecurringExpense;
  onMarkPaid: (id: string) => void;
  isMarking: boolean;
}

const DueSoonCard: React.FC<DueSoonCardProps> = ({ expense, onMarkPaid, isMarking }) => {
  const days = daysUntil(expense.next_due_date);
  const overdue = isOverdue(expense.next_due_date);

  const gradient = overdue
    ? 'from-red-500 via-rose-500 to-pink-500'
    : days <= 2
    ? 'from-orange-500 via-amber-500 to-yellow-500'
    : 'from-amber-400 via-orange-400 to-red-400';

  return (
    <div
      className={`relative overflow-hidden rounded-2xl p-4 text-white shadow-lg
                  bg-gradient-to-br ${gradient}
                  animate-slide-up animate-fill-both`}
    >
      <div className="absolute -top-3 -right-3 w-20 h-20 bg-white/10 rounded-full" />

      <div className="relative z-10 flex items-start gap-3">
        <span className="text-2xl shrink-0">{expense.icon || '\uD83D\uDCE6'}</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{expense.name}</p>
          <p className="text-xs text-white/70 mt-0.5">
            {overdue
              ? `Ueberfaellig seit ${formatDueDate(expense.next_due_date)}`
              : days === 0
              ? 'Heute faellig'
              : `Faellig in ${days} ${days === 1 ? 'Tag' : 'Tagen'}`}
          </p>
          <p className="text-lg font-bold font-mono tabular-nums mt-1">
            {formatEUR(expense.amount)}
          </p>
        </div>
        <button
          onClick={() => onMarkPaid(expense.id)}
          disabled={isMarking}
          aria-label={`${expense.name} als bezahlt markieren`}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/20 backdrop-blur-sm
                     hover:bg-white/30 active:bg-white/10 transition-colors text-xs font-semibold
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isMarking ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Check className="w-3.5 h-3.5" />
          )}
          Bezahlt
        </button>
      </div>
    </div>
  );
};

// =============================================
// Expense Row
// =============================================
interface ExpenseRowProps {
  expense: RecurringExpense;
  index: number;
  onClick: () => void;
  onDelete: () => void;
  onMarkPaid: () => void;
  isDeleting: boolean;
  isMarking: boolean;
}

const ExpenseRow: React.FC<ExpenseRowProps> = ({
  expense,
  index,
  onClick,
  onDelete,
  onMarkPaid,
  isDeleting,
  isMarking,
}) => {
  const soon = isDueSoon(expense.next_due_date);
  const overdue = isOverdue(expense.next_due_date);

  return (
    <div
      className="flex items-center gap-3 px-1 py-2 rounded-xl group
                 hover:bg-white/60 dark:hover:bg-white/5
                 transition-all duration-200 animate-slide-up animate-fill-both"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Icon */}
      <button
        onClick={onClick}
        aria-label={`${expense.name} bearbeiten`}
        className="w-11 h-11 rounded-xl bg-neutral-100 dark:bg-white/5 flex items-center justify-center
                   shrink-0 text-xl hover:scale-105 transition-transform duration-200"
      >
        {expense.icon || '\uD83D\uDCE6'}
      </button>

      {/* Info */}
      <button
        onClick={onClick}
        className="flex-1 min-w-0 text-left"
        aria-label={`${expense.name} bearbeiten`}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate">
            {expense.name}
          </p>
          <span
            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0
                        ${INTERVAL_BADGE_COLORS[expense.interval]}`}
          >
            {INTERVAL_LABELS[expense.interval]}
          </span>
        </div>
        <p
          className={`text-xs mt-0.5 ${
            overdue
              ? 'text-red-500 dark:text-red-400 font-medium'
              : soon
              ? 'text-amber-500 dark:text-amber-400 font-medium'
              : 'text-neutral-400 dark:text-neutral-500'
          }`}
        >
          {overdue ? (
            <span className="flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Ueberfaellig: {formatDueDate(expense.next_due_date)}
            </span>
          ) : (
            `Naechste Zahlung: ${formatDueDate(expense.next_due_date)}`
          )}
        </p>
      </button>

      {/* Amount */}
      <div className="text-right shrink-0">
        <p className="text-sm font-bold font-mono tabular-nums text-neutral-800 dark:text-neutral-100">
          {formatEUR(expense.amount)}
        </p>
        <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-0.5">
          {formatEUR(toMonthlyAmount(expense.amount, expense.interval))}/Mo
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <button
          onClick={(e) => { e.stopPropagation(); onMarkPaid(); }}
          disabled={isMarking}
          aria-label="Als bezahlt markieren"
          className="p-1.5 rounded-lg text-emerald-400 hover:text-emerald-600 hover:bg-emerald-50
                     dark:hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
        >
          {isMarking ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          disabled={isDeleting}
          aria-label={`${expense.name} loeschen`}
          className="p-1.5 rounded-lg text-neutral-300 hover:text-red-500 hover:bg-red-50
                     dark:hover:bg-red-500/10 transition-colors disabled:opacity-50"
        >
          {isDeleting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
        </button>
      </div>

      <ChevronRight
        className="w-4 h-4 text-neutral-300 shrink-0 group-hover:text-primary-400
                   group-hover:translate-x-0.5 transition-all duration-200 hidden sm:block"
        onClick={onClick}
      />
    </div>
  );
};

// =============================================
// Expense Form (shared by Add and Edit modal)
// =============================================
interface ExpenseFormValues {
  name: string;
  amount: string;
  interval: RecurringInterval;
  next_due_date: string;
  category: string;
  icon: string;
  note: string;
}

const EMPTY_FORM: ExpenseFormValues = {
  name: '',
  amount: '',
  interval: 'monthly',
  next_due_date: new Date().toISOString().slice(0, 10),
  category: '',
  icon: '\uD83D\uDCE6',
  note: '',
};

function expenseToForm(e: RecurringExpense): ExpenseFormValues {
  return {
    name: e.name,
    amount: String(e.amount),
    interval: e.interval,
    next_due_date: e.next_due_date,
    category: e.category || '',
    icon: e.icon || '\uD83D\uDCE6',
    note: e.note || '',
  };
}

interface ExpenseFormProps {
  values: ExpenseFormValues;
  onChange: (values: ExpenseFormValues) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading: boolean;
  submitLabel: string;
}

const inputClass =
  'w-full px-4 py-3 text-sm border border-neutral-200 dark:border-indigo-500/20 rounded-xl ' +
  'bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm dark:text-white ' +
  'placeholder:text-neutral-400 dark:placeholder:text-neutral-500 ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 ' +
  'dark:focus:border-indigo-500/40 transition-all duration-200';

const labelClass = 'block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5';

const ExpenseForm: React.FC<ExpenseFormProps> = ({
  values,
  onChange,
  onSubmit,
  onCancel,
  isLoading,
  submitLabel,
}) => {
  const set = (field: keyof ExpenseFormValues) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => onChange({ ...values, [field]: e.target.value });

  const isValid = values.name.trim() !== '' && Number(values.amount) > 0;

  return (
    <div className="space-y-5">
      {/* Icon Picker */}
      <div>
        <p className={labelClass}>Symbol</p>
        <div className="flex flex-wrap gap-2">
          {ICON_OPTIONS.map((opt) => (
            <button
              key={opt.emoji}
              type="button"
              onClick={() => onChange({ ...values, icon: opt.emoji })}
              aria-label={opt.label}
              title={opt.label}
              className={`w-11 h-11 text-xl rounded-xl transition-all duration-200 flex items-center justify-center
                          ${
                            values.icon === opt.emoji
                              ? 'bg-gradient-to-br from-primary-500/20 to-secondary-500/20 ring-2 ring-primary-400 scale-110'
                              : 'bg-neutral-100 dark:bg-white/5 hover:scale-105 hover:bg-primary-50 dark:hover:bg-white/10'
                          }`}
            >
              {opt.emoji}
            </button>
          ))}
        </div>
      </div>

      {/* Name */}
      <div>
        <label htmlFor="exp-name" className={labelClass}>
          Name <span className="text-red-400">*</span>
        </label>
        <input
          id="exp-name"
          type="text"
          value={values.name}
          onChange={set('name')}
          onKeyDown={(e) => e.key === 'Enter' && isValid && onSubmit()}
          placeholder="z.B. Netflix, Miete, Strom..."
          autoFocus
          className={inputClass}
        />
      </div>

      {/* Amount + Interval (row) */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="exp-amount" className={labelClass}>
            Betrag (EUR) <span className="text-red-400">*</span>
          </label>
          <input
            id="exp-amount"
            type="number"
            min="0"
            step="0.01"
            value={values.amount}
            onChange={set('amount')}
            placeholder="0,00"
            className={`${inputClass} text-lg font-semibold`}
          />
        </div>
        <div>
          <label htmlFor="exp-interval" className={labelClass}>
            Intervall
          </label>
          <select
            id="exp-interval"
            value={values.interval}
            onChange={set('interval')}
            className={inputClass}
          >
            {INTERVAL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Due Date */}
      <div>
        <label htmlFor="exp-due" className={labelClass}>
          Naechstes Faelligkeitsdatum
        </label>
        <input
          id="exp-due"
          type="date"
          value={values.next_due_date}
          onChange={set('next_due_date')}
          className={inputClass}
        />
      </div>

      {/* Category */}
      <div>
        <label htmlFor="exp-category" className={labelClass}>
          Kategorie (optional)
        </label>
        <select
          id="exp-category"
          value={values.category}
          onChange={set('category')}
          className={inputClass}
        >
          <option value="">Keine Kategorie</option>
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {/* Note */}
      <div>
        <label htmlFor="exp-note" className={labelClass}>
          Notiz (optional)
        </label>
        <textarea
          id="exp-note"
          value={values.note}
          onChange={set('note')}
          rows={2}
          placeholder="Beliebige Notiz..."
          className={`${inputClass} resize-none`}
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-3 pt-1">
        <Button variant="outline" fullWidth onClick={onCancel}>
          Abbrechen
        </Button>
        <Button
          fullWidth
          loading={isLoading}
          onClick={onSubmit}
          disabled={!isValid}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};

// =============================================
// Add Modal
// =============================================
interface AddModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddModal: React.FC<AddModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<ExpenseFormValues>(EMPTY_FORM);

  const createMutation = useMutation({
    mutationFn: (data: RecurringExpenseCreate) => recurringExpensesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] });
      toast.success('Ausgabe hinzugefuegt');
      onClose();
      setValues(EMPTY_FORM);
    },
    onError: () => {
      toast.error('Fehler', 'Ausgabe konnte nicht gespeichert werden.');
    },
  });

  const handleSubmit = () => {
    if (!values.name.trim() || Number(values.amount) <= 0) return;
    createMutation.mutate({
      name: values.name.trim(),
      amount: Number(values.amount),
      interval: values.interval,
      next_due_date: values.next_due_date,
      category: values.category || undefined,
      icon: values.icon || undefined,
      note: values.note.trim() || undefined,
    });
  };

  const handleClose = () => {
    onClose();
    setValues(EMPTY_FORM);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Neue Ausgabe" size="md">
      <ExpenseForm
        values={values}
        onChange={setValues}
        onSubmit={handleSubmit}
        onCancel={handleClose}
        isLoading={createMutation.isPending}
        submitLabel="Hinzufuegen"
      />
    </Modal>
  );
};

// =============================================
// Edit Modal
// =============================================
interface EditModalProps {
  expense: RecurringExpense | null;
  onClose: () => void;
}

const EditModal: React.FC<EditModalProps> = ({ expense, onClose }) => {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<ExpenseFormValues>(
    expense ? expenseToForm(expense) : EMPTY_FORM,
  );

  // Sync when expense changes
  React.useEffect(() => {
    if (expense) setValues(expenseToForm(expense));
  }, [expense]);

  const updateMutation = useMutation({
    mutationFn: (data: RecurringExpenseUpdate) =>
      recurringExpensesApi.update(expense!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] });
      toast.success('Ausgabe aktualisiert');
      onClose();
    },
    onError: () => {
      toast.error('Fehler', 'Ausgabe konnte nicht gespeichert werden.');
    },
  });

  const handleSubmit = () => {
    if (!expense || !values.name.trim() || Number(values.amount) <= 0) return;
    updateMutation.mutate({
      name: values.name.trim(),
      amount: Number(values.amount),
      interval: values.interval,
      next_due_date: values.next_due_date,
      category: values.category || undefined,
      icon: values.icon || undefined,
      note: values.note.trim() || undefined,
    });
  };

  return (
    <Modal isOpen={expense !== null} onClose={onClose} title="Ausgabe bearbeiten" size="md">
      <ExpenseForm
        values={values}
        onChange={setValues}
        onSubmit={handleSubmit}
        onCancel={onClose}
        isLoading={updateMutation.isPending}
        submitLabel="Speichern"
      />
    </Modal>
  );
};

// =============================================
// Empty State
// =============================================
const EmptyState: React.FC<{ onAdd: () => void }> = ({ onAdd }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center animate-fade-in">
    <div className="w-20 h-20 mb-5 gradient-primary rounded-3xl flex items-center justify-center shadow-glow-primary">
      <Repeat className="w-10 h-10 text-white" />
    </div>
    <h2 className="text-xl font-bold text-neutral-800 dark:text-white mb-2">
      Noch keine Fixkosten
    </h2>
    <p className="text-neutral-500 dark:text-neutral-400 text-sm mb-8 max-w-xs">
      Fuge Miete, Abonnements und andere wiederkehrende Ausgaben hinzu, um den Ueberblick zu behalten.
    </p>
    <button
      onClick={onAdd}
      aria-label="Neue Ausgabe hinzufuegen"
      className="w-20 h-20 rounded-full gradient-primary shadow-glow-primary
                 flex items-center justify-center
                 hover:scale-105 active:scale-95 transition-transform duration-200"
    >
      <Plus className="w-10 h-10 text-white" />
    </button>
    <p className="mt-4 text-sm text-neutral-400 dark:text-neutral-500 font-medium">
      Neue Ausgabe hinzufuegen
    </p>
  </div>
);

// =============================================
// Page
// =============================================
export const RecurringExpensesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editingExpense, setEditingExpense] = useState<RecurringExpense | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [markingIds, setMarkingIds] = useState<Set<string>>(new Set());

  const {
    data: expenses = [],
    isLoading,
    isError,
  } = useQuery<RecurringExpense[]>({
    queryKey: ['recurring-expenses'],
    queryFn: recurringExpensesApi.list,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => recurringExpensesApi.delete(id),
    onMutate: (id) => {
      setDeletingIds((prev) => new Set(prev).add(id));
      // Optimistic remove
      queryClient.setQueryData<RecurringExpense[]>(['recurring-expenses'], (old) =>
        old ? old.filter((e) => e.id !== id) : [],
      );
    },
    onSuccess: (_data, id) => {
      setDeletingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
      queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] });
      toast.success('Ausgabe geloescht');
    },
    onError: (_err, id) => {
      setDeletingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
      queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] });
      toast.error('Fehler', 'Ausgabe konnte nicht geloescht werden.');
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: (id: string) => recurringExpensesApi.markPaid(id),
    onMutate: (id) => {
      setMarkingIds((prev) => new Set(prev).add(id));
    },
    onSuccess: (updated, id) => {
      setMarkingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
      // Update in cache
      queryClient.setQueryData<RecurringExpense[]>(['recurring-expenses'], (old) =>
        old ? old.map((e) => (e.id === id ? updated : e)) : [],
      );
      toast.success('Als bezahlt markiert');
    },
    onError: (_err, id) => {
      setMarkingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
      toast.error('Fehler', 'Konnte nicht als bezahlt markiert werden.');
    },
  });

  if (isLoading) {
    return <LoadingSpinner fullPage text="Fixkosten werden geladen..." />;
  }

  if (isError) {
    return (
      <Card>
        <p className="text-center py-8 text-red-500">
          Daten konnten nicht geladen werden. Bitte versuche es erneut.
        </p>
      </Card>
    );
  }

  const active = expenses.filter((e) => e.is_active);
  const summary = computeSummary(active);

  const dueSoon = active
    .filter((e) => isDueSoon(e.next_due_date) || isOverdue(e.next_due_date))
    .sort((a, b) => new Date(a.next_due_date).getTime() - new Date(b.next_due_date).getTime());

  const allSorted = [...active].sort(
    (a, b) => new Date(a.next_due_date).getTime() - new Date(b.next_due_date).getTime(),
  );

  if (active.length === 0) {
    return (
      <>
        <div className="space-y-6">
          <SummaryHeader
            monthlyTotal={0}
            yearlyTotal={0}
            count={0}
          />
          <EmptyState onAdd={() => setShowAdd(true)} />
        </div>
        <AddModal isOpen={showAdd} onClose={() => setShowAdd(false)} />
      </>
    );
  }

  return (
    <>
      <div className="space-y-8">
        {/* Summary Header */}
        <SummaryHeader
          monthlyTotal={summary.monthly_total}
          yearlyTotal={summary.yearly_total}
          count={summary.count}
        />

        {/* Due Soon Section */}
        {dueSoon.length > 0 && (
          <section aria-labelledby="due-soon-heading" className="animate-slide-up animate-fill-both" style={{ animationDelay: '200ms' }}>
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-4 h-4 text-amber-500" aria-hidden="true" />
              <h2 id="due-soon-heading" className="text-sm font-bold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                Bald faellig
              </h2>
              <span className="ml-auto text-xs font-semibold bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full">
                {dueSoon.length}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {dueSoon.map((expense) => (
                <DueSoonCard
                  key={expense.id}
                  expense={expense}
                  onMarkPaid={(id) => markPaidMutation.mutate(id)}
                  isMarking={markingIds.has(expense.id)}
                />
              ))}
            </div>
          </section>
        )}

        {/* All Expenses List */}
        <section aria-labelledby="all-expenses-heading">
          <Card padding="none">
            {/* Card Header */}
            <div className="p-5 pb-3 flex items-center justify-between">
              <h2
                id="all-expenses-heading"
                className="text-lg font-bold text-neutral-800 dark:text-white flex items-center gap-2"
              >
                <Repeat className="w-5 h-5 text-primary-500" aria-hidden="true" />
                Alle Ausgaben
              </h2>
              <Button
                icon={<Plus className="w-4 h-4" />}
                onClick={() => setShowAdd(true)}
                size="sm"
              >
                Hinzufuegen
              </Button>
            </div>

            <div className="px-3 pb-3 divide-y divide-neutral-100 dark:divide-white/5">
              {allSorted.map((expense, index) => (
                <div key={expense.id} className="py-1">
                  <ExpenseRow
                    expense={expense}
                    index={index}
                    onClick={() => setEditingExpense(expense)}
                    onDelete={() => deleteMutation.mutate(expense.id)}
                    onMarkPaid={() => markPaidMutation.mutate(expense.id)}
                    isDeleting={deletingIds.has(expense.id)}
                    isMarking={markingIds.has(expense.id)}
                  />
                </div>
              ))}
            </div>
          </Card>
        </section>

        {/* Bottom FAB spacer */}
        <div className="h-4" aria-hidden="true" />
      </div>

      {/* FAB — Mobile */}
      <button
        onClick={() => setShowAdd(true)}
        aria-label="Neue Ausgabe hinzufuegen"
        className="fixed bottom-20 right-4 z-20 lg:hidden
                   w-14 h-14 rounded-full gradient-primary shadow-glow-primary text-white
                   flex items-center justify-center
                   hover:scale-105 active:scale-95 transition-transform duration-200"
      >
        <Plus className="w-7 h-7" />
      </button>

      {/* Modals */}
      <AddModal isOpen={showAdd} onClose={() => setShowAdd(false)} />
      <EditModal expense={editingExpense} onClose={() => setEditingExpense(null)} />
    </>
  );
};
